import {
	Button,
	Checkbox,
	Col,
	ConfigProvider,
	Form,
	Input,
	Row,
	Select,
	Space,
	Typography
} from 'antd'
import React, { useRef } from 'react'
import { GeneralTitle } from '../../../components/Shared'
import { actApplyForJob } from '../../../stores/users/actions'
import { observer } from 'mobx-react-lite'
import { GeneralContentWrapper } from '../../../components/Shared/GeneralComponents'
import FileUpload from '../../../components/Shared/FileUpload'
import { v4 as uuidv4 } from 'uuid'
import { translate, useSmartTranslation } from '../../../cls/TranslationContext'
const { Paragraph } = Typography

/**
 * @constant highestEducationOptions
 * @description List of options for the highest education field in the form.
 */
export const highestEducationOptions = [
	{
		key: 'highest_education',
		label: translate('__select.please%single.highest-education'),
		value: ''
	},
	{
		key: 'highest_education-high-school',
		label: translate('single.high-school'),
		value: 'High School'
	},
	{
		key: 'highest_education-bachelors',
		label: translate('single.bachelor'),
		value: "Bachelor's"
	},
	{
		key: 'highest_education-masters',
		label: translate('single.master'),
		value: "Master's"
	}
]

/**
 * @function RecruitmentApply
 * @description Renders the public recruitment application form for external users.
 * Includes fields for name, contact, education, social links, and CV upload.
 * Submits data to the backend and provides real-time feedback upon submission.
 */
const RecruitmentApply = () => {
	const { t } = useSmartTranslation()
	const [form] = Form.useForm()
	const messageRef = useRef(null)
	const recruitment_id = uuidv4()

	// Set initial form values based on the logged-in user
	/**
	 * @constant _initialFormValues
	 * @description Default values used to initialize the recruitment application form.
	 */
	const _initialFormValues = {
		name: '',
		email: '',
		phone: '',
		highest_education: '',
		cv: [],
		linkedin: '',
		facebook: '',
		website: ''
	}

	/**
	 * Handles form submission for saving contact form data
	 *
	 * @param {Object} vals - The form values.
	 */
	const _doSave = (vals) => {
		if (vals !== null) {
			actApplyForJob(recruitment_id, vals)
				.then((response) => {
					console.log('response', response)

					if (!response) {
						// Set error message
						messageRef.current.style.color = 'red'
						messageRef.current.textContent =
							response?.message || t('single.fail-to-apply')
						return
					}

					// Set success message
					messageRef.current.style.color = 'green'
					messageRef.current.textContent =
						response?.message || t('single.applied-successfully')

					// Clear the form
					form.resetFields()
				})
				.catch((error) => {
					console.log('error', error)

					// Set error message
					messageRef.current.style.color = 'red'
					messageRef.current.textContent =
						error?.message || t('single.fail-to-apply') // Same problem. find if error.message key exists. somebody added it as response.message
				})
		}
	}

	// Handle file upload change
	/**
	 * @function handleFileChange
	 * @description Updates the form state with uploaded CV file identifiers.
	 *
	 * @param {Array<string>} fileList - List of file IDs returned from the FileUpload component.
	 */
	const handleFileChange = (fileList) => {
		form.setFieldsValue({ cv: fileList })
	}

	return (
		<GeneralContentWrapper>
			<GeneralTitle text={t('single.apply-job.title')} />
			<ConfigProvider>
				<Form
					layout="vertical"
					form={form}
					name="recruitment-apply"
					initialValues={_initialFormValues}
					onFinish={_doSave}
				>
					<Row
						gutter={[50, 50]}
						justify="space-between"
						align="middle"
					>
						<Col xs={24} sm={24} md={17} lg={17}>
							<Space
								style={{ width: '100%' }}
								direction="vertical"
							>
								<Paragraph>
									{t('single.apply-job.fill-form')}
								</Paragraph>

								<div>
									<Form.Item
										label={t('single.name')}
										tooltip={t('__enter.please%single.name')}
										name="name"
										rules={[
											{
												required: true,
												message: t(
													'__provide.please%single.name'
												)
											}
										]}
									>
										<Input
											placeholder={t(
												'__provide.please%single.name'
											)}
										/>
									</Form.Item>
									<Row gutter={[20, 20]}>
										<Col xs={24} sm={24} md={12} lg={12}>
											<Form.Item
												label="Email"
												tooltip={t(
													'single.reach.email'
												)}
												name="email"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%_email.address'
														)
													},
													{
														type: 'email',
														message: t(
															'single.invalid.email'
														)
													}
												]}
											>
												<Input
													placeholder={t(
														'__provide.please%_email.address'
													)}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} sm={24} md={12} lg={12}>
											<Form.Item
												label={t('single.phone')}
												tooltip={t(
													'single.invalid.phone'
												)}
												name="phone"
												rules={[
													{
														required: true,
														message: t(
															'__provide.please%single.phone-number'
														)
													}
												]}
											>
												<Input
													placeholder={t(
														'__provide.please%single.phone-number'
													)}
												/>
											</Form.Item>
										</Col>
									</Row>

									<Row gutter={[20, 20]}>
										<Col xs={24} sm={24} md={12} lg={12}>
											<Form.Item
												label={t(
													'single.highest-education'
												)}
												tooltip={t(
													'__select.please%single.highest-education'
												)}
												name="highest_education"
												rules={[
													{
														required: true,
														message: t(
															'__select.please%single.highest-education'
														)
													}
												]}
											>
												<Select
													options={
														highestEducationOptions
													}
												/>
											</Form.Item>
										</Col>
										<Col xs={24} sm={24} md={12} lg={12}>
											<Form.Item
												label="CV"
												tooltip={t(
													'single.upload-cv'
												)}
												name="cv"
												rules={[
													{
														required: true,
														message: t(
															'single.upload-cv'
														)
													}
												]}
											>
												<FileUpload
													entity_table="recruitments"
													entity_uid={recruitment_id}
													onChange={handleFileChange}
												/>
											</Form.Item>
										</Col>
									</Row>
									<Form.Item
										label="Linkedin URL"
										tooltip={t('__please.provide-profile-url%platform.linkedin.label')}
										name="linkedin"
									>
										<Input
											placeholder={t(
												'__please.provide-profile-url%platform.linkedin.label'
											)}
										/>
									</Form.Item>
									<Form.Item
										label="Facebook URL"
										tooltip={t('__please.provide-profile-url%platform.facebook.label')}
										name="facebook"
									>
										<Input
											placeholder={t(
												'__please.provide-profile-url%platform.facebook.label'
											)}
										/>
									</Form.Item>
									<Form.Item
										label={t(
											'single.portfolio-website'
										)}
										tooltip={t('single.provide-url-website')}
										name="website"
									>
										<Input
											placeholder={t(
												'single.provide-url-website'
											)}
										/>
									</Form.Item>

									<Form.Item
										name="consent"
										valuePropName="checked"
										rules={[
											{
												validator: (_, value) =>
													value
														? Promise.resolve()
														: Promise.reject(
																new Error(
																	t(
																		'single.agree.terms-and-conditions'
																	)
																)
														  )
											}
										]}
									>
										<Checkbox>
											{t('single.agree.read-and-agree')}
										</Checkbox>
									</Form.Item>

									<Form.Item style={{ marginBottom: 0 }}>
										<Button
											type="primary"
											htmlType="submit"
										>
											{t('single.submit')}
										</Button>
										<span
											ref={messageRef}
											style={{ marginLeft: 16 }}
										></span>
									</Form.Item>
								</div>
							</Space>
						</Col>
						<Col xs={24} sm={24} md={7} lg={7}>
							<img
								width="100%"
								src={`${process.env.PUBLIC_URL}/webackit-logo-color.svg`}
								alt="webackit"
							/>
						</Col>
					</Row>
				</Form>
			</ConfigProvider>
		</GeneralContentWrapper>
	)
}

export default observer(RecruitmentApply)
